import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { IPaginationPayload } from "../../../interfaces/shared/IPaginationPayload";
import { IToast } from '../../../interfaces/components/IToast';
import { tryToFetchInvoices, tryToMatchReceivableInvoices } from "../../../store/brokers/partner/invoices/invoicesSlice";
import { useGetXeroAuthUrlQuery } from "../../../store/reservations/reservationsApi";
import { useGetCurrentUserQuery } from "../../../store/user/userApi";
import InvoiceDetailsModal from "./modals/InvoiceDetailsModal";
import CategorizeModal from "./modals/CategorizeModal";
import ReceivableTable from "./tables/ReceivableTable";
import InvoiceFilters from "./details/InvoiceFilters";
import Button from "../../../shared/button";
import Input from "../../../shared/input";
import Toast from '../../../shared/toast';


const AccountReceivable = () => {
    const dispatch = useAppDispatch();
    const [userInfoData, setUserInfoData] = useState<any>(localStorage.getItem('user'))
    const { data: userInfo } = useGetCurrentUserQuery();
    const [rows, setRows] = useState<any>([]);
    const [search, setSearch] = useState<string>();
    const [showToast, setShowToast] = useState<IToast | null>();
    const [statusFilter, setStatusFilter] = useState<any>();
    const [dueDateFilter, setDueDateFilter] = useState<any>();
    const [tableLoading, setTableLoading] = useState<boolean>(true);
    const [matchingLoading, setMatchingLoading] = useState<boolean>(false);
    const [showCategorizeModal, setShowCategorizeModal] = useState<boolean>();
    const [showDetailsModal, setShowDetailsModal] = useState<{ show: boolean, data: any }>()
    const [paginationState, setPaginationState] = useState<IPaginationPayload>({
        pageNumber: 1,
        pageSize: 10,
    });
    const { data: authUrl } = useGetXeroAuthUrlQuery({});
    const paginationData = useAppSelector((state) => state.hotelInvoices.invoices?.data?.page);
    const rowsLoading = useAppSelector((state) => state.hotelInvoices.invoicesAreLoading);
    const state = useAppSelector((state) => state.hotelInvoices);

    const onResetPaginationState = () => {
        setPaginationState({
            pageNumber: 1,
            pageSize: paginationState?.pageSize || 10,
        })
    }

    const onChangeSearchValue = (e: any) => {
        setSearch(e)
        onResetPaginationState()
    }

    const isJsonObject = (jsonString: string) => {
        try {
            const o = JSON.parse(jsonString);
            if (o && typeof o === 'object') {
                return true;
            }
        }
        catch (e) { }
        return false;
    };

    const getCurrentUserData = async () => {
        const getUserLocal: any = localStorage.getItem('user')
        const userFormat: any = isJsonObject(getUserLocal) ? JSON.parse(getUserLocal) : undefined
        if (getUserLocal && (!userFormat && !userFormat?.username)) {
            setUserInfoData(getUserLocal)
        } else {
            setUserInfoData(userInfo?.username)
            localStorage.setItem('user', userInfo?.username || '');
        }
    }

    useEffect(() => {
        getCurrentUserData()
    }, [userInfo])

    useEffect(() => {
        if (state.invoices) {
            const rows =
                state.invoices?.data?.elements && Array.isArray(state.invoices?.data.elements)
                    ? state.invoices?.data.elements.map((r: any) => ({ ...r, id: r._id }))
                    : [];
            setRows(rows);
        }
    }, [state.invoices]);

    const onGettingData = async () => {
        try {
            const accessToken = localStorage.getItem("xeroAccessToken");
            if (accessToken) {
                let data: any = { ...paginationState, data: { type: 'ACCREC', username: userInfoData } }
                if (search) {
                    data = {
                        ...data || {},
                        data: {
                            ...data?.data || {},
                            search: search
                        }
                    }
                }
                if (dueDateFilter) {
                    data = {
                        ...data || {},
                        data: {
                            ...data?.data || {},
                            dueDate: dueDateFilter
                        }
                    }
                }
                if (statusFilter) {
                    data = {
                        ...data || {},
                        data: {
                            ...data?.data || {},
                            status: statusFilter?.value
                        }
                    }
                }
                await dispatch(tryToFetchInvoices(data)).unwrap();
                setTableLoading(false)
            } else {
                window.location.assign(authUrl.authUrl);
            }
        } catch (err) {
            // err
            setTableLoading(false)
        }
    }

    useEffect(() => {
        onGettingData();
    }, [paginationState]);

    const onSeeDetails = async (data: any) => {
        setShowDetailsModal({
            show: true,
            data: data
        })
        document.body.style.overflow = 'hidden';
    }

    const onCloseModal = () => {
        setShowDetailsModal(undefined)
        document.body.style.overflow = 'auto';
    }

    const onChangeFilter = (value: any, type?: string) => {
        if (type === 'status') {
            setStatusFilter(value)
        } else {
            setDueDateFilter(value)
        }
        onResetPaginationState()
    }

    const onMatchingPayments = async () => {
        setMatchingLoading(true)
        try {
            const data = {
                username: userInfoData
            }
            const res: any = await dispatch(tryToMatchReceivableInvoices(data)).unwrap();
            onResetPaginationState()
            setShowToast({ type: 'success', message: `Successfully processed ${res?.totalProcessed}` })
        } catch (err) {
            setShowToast({ type: 'error', message: `${err}` })
        }
        setMatchingLoading(false)
    }

    const onSeeCategorizeModal = () => {
        if (showCategorizeModal) {
            document.body.style.overflow = 'auto';
        } else {
            document.body.style.overflow = 'hidden';
        }
        setShowCategorizeModal(!showCategorizeModal)
    }

    const onClickClearAllFilter = () => {
        setSearch(undefined)
        setStatusFilter(undefined)
        setDueDateFilter(undefined)
        onResetPaginationState()
    }

    return (
        <div>
            <div>
                <InvoiceFilters
                    statusFilter={statusFilter}
                    dueDateFilter={dueDateFilter}
                    onClickFilter={onChangeFilter}
                />
            </div>
            <div className="flex flex-row justify-end md:justify-end my-3">
                <Button
                    label={'Match Payments'}
                    icon={<img alt="reviewt" className="mr-2" src="/assets/shared/reviewt.svg" />}
                    className={`btn-primary-rounded-less mr-2 flex flex-row w-auto ${matchingLoading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    secondLoading={matchingLoading}
                    onClickButton={() => !matchingLoading && onMatchingPayments()}
                />
                <Button
                    label={'Categorize Invoices'}
                    className="btn-primary-rounded-less"
                    onClickButton={onSeeCategorizeModal}
                />
            </div>
            <div className='my-5 relative'>
            <div className={`flex flex-col md:flex-row md:justify-between md:items-center bg-call-default p-1 !rounded`}>
                    <div>
                        <Input
                            placeholder={'Search for Invoices'}
                            dataQa={'search'}
                            containerStyle='mb-0 pb-0 relative min-w-[400px]'
                            inputStyle={`mb-0 border-0 focus:outline-none`}
                            icon={'/assets/shared/search.svg'}
                            showValue={true}
                            inputValue={search}
                            inputUniqueName={'search'}
                            onChangeInput={onChangeSearchValue}
                            hideInputSpace={true}
                        />
                    </div>
                    <div className='flex flex-row justify-end ml-2'>
                        <Button
                            label='Clear Filters'
                            className={'btn-primary-text-underline min-w-auto'}
                            onClickButton={() => onClickClearAllFilter()}
                        />
                    </div>
                </div>
                <ReceivableTable
                    rows={rows}
                    rowsLoading={rowsLoading || tableLoading}
                    paginationData={paginationData}
                    onSeeDetails={onSeeDetails}
                    setPaginationState={setPaginationState}
                />
            </div>
            {showDetailsModal?.show &&
                <InvoiceDetailsModal
                    openModal={showDetailsModal?.show}
                    data={showDetailsModal?.data}
                    handleCloseModal={onCloseModal}
                />
            }
            {showToast?.message &&
                <Toast
                    type={showToast?.type}
                    text={showToast?.message}
                    onHandleCloseToast={() => setShowToast(null)}
                />
            }
            {showCategorizeModal &&
                <CategorizeModal
                    openModal={showCategorizeModal}
                    username={`${userInfoData || ''}`}
                    type={'receivable'}
                    handleCloseModal={onSeeCategorizeModal}
                />
            }
        </div>
    )
}
export default AccountReceivable;
